import React, { useEffect, useMemo } from "react";

import {
  CodeIcon as Code,
  LeafIcon as Leaf,
  Scrollbox,
  Skeleton,
  Typography
} from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import EnvironmentImpact from "Containers/EnvironmentImpact";
import { Link as LinkButton } from "ds/Button";
import * as Dialog from "ds/Dialog";
import Tag from "ds/Tag";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import {
  getSubscriptionEditUrl,
  roundedNumber,
  splitStringByChar
} from "Libs/utils";
import { selectProjectAccesses } from "Reducers/access/projectAccess/projectAccess.selectors";
import { getProjectAccessesThunk } from "Reducers/access/projectAccess/thunks/getProjectAccesses.thunk";
import { useShowHipaa } from "Reducers/app/hooks/useShowHipaa";
import { environmentSelector } from "Reducers/environment";
import {
  currentDeploymentSelector,
  deploymentResourcesSelector,
  deploymentSizingApiEnabledSelector,
  isLoadingSelector,
  isUpdateLoadingSelector
} from "Reducers/environment/deployment";
import { selectUsedEnvironments } from "Reducers/environment/environment.selectors";
import {
  selectFeatureEditPlanEnabled,
  selectFeatureProjectSetupWizardEnabled,
  selectFeatureUsageBasedBillingEnabled,
  selectExternalUrlEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import {
  gitProjectSelector,
  selectProjectStatus
} from "Reducers/project/project";
import {
  getRegions,
  regionSelector,
  regionLabelSelector,
  isGreenRegion
} from "Reducers/project/region";
import { openProjectWizard } from "Reducers/project/wizard";
import { setProjectContext } from "Reducers/settingsMenu";
import { setupOptionsPlanTitlesSelector } from "Reducers/setupOptions/setupOptions.selectors";
import { subscriptionSelector } from "Reducers/subscription";
import ConfigureResourcesButton from "src/organization/pages/billing/projectBilling/components/ConfigureResourcesButton";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import LinkPreview from "../LinkPreview/LinkPreview";

import * as S from "./ProjectDetails.styles";

export const getMaxDevelopmentEnvironmentNumber = project => {
  if (!project?.subscription) {
    return "";
  }
  const env =
    project.subscription.resources?.development?.max_environments ||
    project.subscription.environments;
  if (env) return env + 1;

  return "";
};

const MAX_AVATARS = 5;

const ProjectDetails = () => {
  const location = useLocation();
  const { organizationId, projectId } = useParams();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const editPlanEnabled = useAppSelector(selectFeatureEditPlanEnabled);
  const projectSetupWizardEnabled = useAppSelector(
    selectFeatureProjectSetupWizardEnabled
  );
  const usageBasedBillingEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  const externalUrlEnabled = useAppSelector(selectExternalUrlEnabled);

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const usedEnvironments = useSelectorWithUrlParams(selectUsedEnvironments);
  const subscription = useAppSelector(state => {
    return subscriptionSelector(state, {
      organizationId,
      projectId,
      id: project?.getSubscriptionId()
    });
  });

  const planTitles = useAppSelector(setupOptionsPlanTitlesSelector);

  const [resourcesUrl] = useUrls(
    "organization.project.environment.configureResources",
    {
      environmentId: project?.default_branch || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  );

  const planProjectTitle =
    planTitles?.[subscription?.plan] ?? subscription?.plan;

  const projectAccesses = useAppSelector(state =>
    selectProjectAccesses(state, { organizationId, projectId })
  );
  const region = useAppSelector(state =>
    regionSelector(state, { regionId: project?.region })
  );
  const regionLabel = useSelectorWithUrlParams(regionLabelSelector);
  const mainEnv = useSelectorWithUrlParams(environmentSelector, {
    environmentId: project?.default_branch
  });

  const showHipaa = useShowHipaa({ projectId, subscription });

  const deployment = useAppSelector(state =>
    currentDeploymentSelector(state, {
      organizationId,
      projectId,
      environmentId: project?.default_branch
    })
  );

  const isDeploymentLoading = useAppSelector(isLoadingSelector);
  const isDeploymentUpdateLoading = useAppSelector(isUpdateLoadingSelector);

  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId: mainEnv?.id
    })
  );

  const resources = useAppSelector(state =>
    deploymentResourcesSelector(state, {
      organizationId,
      projectId,
      environmentId: mainEnv?.id
    })
  );

  const hasProjectRoutes = mainEnv?.params?._links?.["pf:routes"]?.length !== 0;
  const projectStatus = useSelectorWithUrlParams(selectProjectStatus);

  const mainUrl = useMemo(() => {
    if (mainEnv?.has_deployment !== true) return;

    const deploymentUrl =
      deployment?.routes &&
      Object.entries(deployment.routes).find(
        ([, values]) => values.primary && values.type !== "redirect"
      );
    if (deploymentUrl) return deploymentUrl[0];

    const url = project.default_domain || mainEnv?.edge_hostname;
    if (url) return `https://${url}`;
  }, [mainEnv, project, deployment]);

  const domainsUrl = `/${organizationId}/${projectId}/-/settings/domains`;

  const navigateToSettingsDomains = e => {
    e.preventDefault();
    dispatch(setProjectContext());
    navigate(domainsUrl);
  };

  useEffect(() => {
    dispatch(getRegions());
  }, []);

  useEffect(() => {
    if (project?.id)
      dispatch(getProjectAccessesThunk({ organizationId, project }));
  }, [project]);

  const subscriptionEditUrl = useMemo(
    () => getSubscriptionEditUrl({ subscription }),
    [subscription]
  );

  const isGreen = isGreenRegion(region);

  const openWizard = () => {
    dispatch(openProjectWizard({ organizationId, projectId }));
  };

  const numberOfUsedEnvironments = usedEnvironments.length;
  const maxDevEnv = getMaxDevelopmentEnvironmentNumber(project);

  if (!project) return <S.Loading />;

  const { pathname } = splitStringByChar(subscriptionEditUrl?.url, "#");
  const internalProps = {
    to: pathname,
    state: { from: "project", url: location.pathname }
  };
  const externalProps = {
    href: subscriptionEditUrl?.url
  };
  const linkProps = externalUrlEnabled ? externalProps : internalProps;

  const isDedicated = subscription?.enterprise_tag;

  let environmentCell = maxDevEnv ? (
    <>
      <S.Value aria-labelledby="projectoverview-environments">
        {intl.formatMessage(
          {
            id: "project.details.environments_used"
          },
          {
            used: numberOfUsedEnvironments,
            total: maxDevEnv
          }
        )}
      </S.Value>
      <S.Label id="projectoverview-environments">
        {intl.formatMessage({ id: "project.details.environments" })}
      </S.Label>
    </>
  ) : null;

  if (isDeploymentLoading || isDeploymentUpdateLoading) {
    environmentCell = (
      <S.Layout synchronize>
        <S.Row>
          <Skeleton.Square height="80%" />
        </S.Row>
      </S.Layout>
    );
  } else if (isSizingApiEnabled) {
    environmentCell = (
      <>
        <S.Value aria-labelledby="projectoverview-environments">
          {numberOfUsedEnvironments}
        </S.Value>
        <S.Label id="projectoverview-environments">
          {intl.formatMessage({
            id:
              numberOfUsedEnvironments > 1
                ? "project.details.environments"
                : "project.details.environment"
          })}
        </S.Label>
      </>
    );
  }

  return (
    <S.Wrapper>
      <S.Details>
        <S.ProjectTitle id="project-name" tag="h3">
          {project.title || intl.formatMessage({ id: "no_title" })}
        </S.ProjectTitle>

        {isDedicated && (
          <S.DedicatedTag>
            {intl.formatMessage({ id: "dedicated" })}
          </S.DedicatedTag>
        )}

        <br></br>

        {showHipaa && (
          <Tag variant="blue">
            {intl.formatMessage({
              id: "hipaa",
              defaultMessage: "HIPAA"
            })}
          </Tag>
        )}

        {subscription?.status === "suspended" && (
          <Tag variant="red">
            {intl.formatMessage({
              id: "project.card.tag.suspended",
              defaultMessage: "Suspended"
            })}
          </Tag>
        )}

        <S.IdRegion>
          <Dialog.Root>
            <Dialog.Trigger analyticId="environmentImpact_project">
              {isGreen && (
                <S.IconControlRegion isGreen={isGreen} aria-label="leaf">
                  <Leaf />
                </S.IconControlRegion>
              )}
            </Dialog.Trigger>
            <Dialog.Content>
              <S.ContentWrapper>
                <EnvironmentImpact inModal={true} regionId={region?.id} />
              </S.ContentWrapper>
            </Dialog.Content>
          </Dialog.Root>

          <Typography.BodyText
            size="extraSmall"
            weight="semiBold"
            $color="granite"
          >
            {regionLabel &&
              `${regionLabel.title || project.region} ${regionLabel.suffix}`}
            {region?.provider?.name && ` - ${region.provider.name}`},{" "}
            {project.id}
          </Typography.BodyText>
        </S.IdRegion>

        {!project.default_domain && projectSetupWizardEnabled && (
          <S.Wizard>
            <LinkButton
              content="mixed"
              onClick={openWizard}
              analyticId="project.overview.wizard"
            >
              <Code />
              {intl.formatMessage({ id: "project.overview.wizard" })}
            </LinkButton>
          </S.Wizard>
        )}

        {/* Plan */}
        {!usageBasedBillingEnabled && !isDedicated && (
          <S.Plan data-testid="plan">
            {intl.formatMessage(
              {
                id: "project.details.plan"
              },
              { name: planProjectTitle }
            )}

            {editPlanEnabled && subscriptionEditUrl && (
              <>
                {subscriptionEditUrl.external ? (
                  <LinkButton
                    href={subscriptionEditUrl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    analyticId="project.details.plan.upgrade"
                  >
                    {intl.formatMessage({
                      id: "project.details.plan.upgrade"
                    })}
                  </LinkButton>
                ) : (
                  <LinkButton
                    {...linkProps}
                    analyticId="project.details.plan.upgrade"
                  >
                    {intl.formatMessage({
                      id: "project.details.plan.upgrade"
                    })}
                  </LinkButton>
                )}
              </>
            )}
          </S.Plan>
        )}
        <S.Infos>
          {/* Storage */}
          {typeof isSizingApiEnabled === "boolean" && !isSizingApiEnabled && (
            <S.Info>
              <S.Value aria-labelledby="projectoverview-storage">
                {project.subscription &&
                  `${project.subscription.storage / 1024} GB`}
              </S.Value>
              <S.Label id="projectoverview-storage">
                {intl.formatMessage({ id: "project.details.disk" })}
              </S.Label>
            </S.Info>
          )}

          {/* Nb of environments */}
          {environmentCell && <S.Info>{environmentCell}</S.Info>}

          {/* Nb of Users */}
          <S.Info>
            <S.Value aria-labelledby="projectoverview-users">
              {project.subscription?.user_licenses}
            </S.Value>
            <S.Label id="projectoverview-users">
              {intl.formatMessage({ id: "project.details.users" })}
            </S.Label>
          </S.Info>

          {isSizingApiEnabled && resources && (
            <>
              <S.Info>
                <S.Value>
                  {intl.formatMessage(
                    {
                      id: "project.details.double_value"
                    },
                    {
                      value: `${roundedNumber(resources.cpu, 1)} CPU,`,
                      value2: `${roundedNumber(resources.ram, 1)} GB RAM`,
                      separator: () => " "
                    }
                  )}
                </S.Value>
                <S.Label id="projectoverview-environments">
                  {intl.formatMessage({ id: "project.details.resources" })}
                </S.Label>
              </S.Info>

              <S.Info>
                <S.Value>
                  {intl.formatMessage(
                    {
                      id: "project.details.double_value"
                    },
                    {
                      value: resources.disk,
                      separator: () => " ",
                      value2: "GB"
                    }
                  )}
                  {mainEnv?.deployment_state && (
                    <S.ConfigureResourcesButtonWrapper>
                      <ConfigureResourcesButton
                        environmentConfigUrl={resourcesUrl}
                      />
                    </S.ConfigureResourcesButtonWrapper>
                  )}
                </S.Value>
                <S.Label id="projectoverview-environments">
                  {intl.formatMessage({ id: "project.details.disk" })}
                </S.Label>
              </S.Info>
            </>
          )}

          {projectAccesses?.length > 1 && (
            <S.Users>
              {projectAccesses.slice(0, MAX_AVATARS).map(access => (
                <S.Avatar
                  key={access?.id}
                  userName={access?.getUser()?.display_name}
                  size="small"
                  withToolTip
                />
              ))}

              {projectAccesses.length > MAX_AVATARS && (
                <S.UsersMore>
                  <span>+{projectAccesses.length - MAX_AVATARS}</span>
                  <S.UsersFullList>
                    <Scrollbox style={{ height: "183px" }}>
                      {projectAccesses.slice(MAX_AVATARS).map(access => (
                        <S.User key={access?.id}>
                          {access?.getUser()?.display_name}
                        </S.User>
                      ))}
                    </Scrollbox>
                  </S.UsersFullList>
                </S.UsersMore>
              )}
            </S.Users>
          )}
        </S.Infos>
      </S.Details>

      <LinkPreview
        hasProjectRoutes={hasProjectRoutes}
        isInactive={
          projectStatus === "inactive" || mainEnv?.status === "inactive"
        }
        mainUrl={mainUrl}
        domainsUrl={domainsUrl}
        onDomainLinkClick={navigateToSettingsDomains}
        projectDefaultBranch={project?.default_branch}
        projectDefaultDomain={project?.default_domain}
        organizationId={organizationId}
        projectId={projectId}
      />
    </S.Wrapper>
  );
};

export default ProjectDetails;
